const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const normalizeQuantity = (value) => {
  const quantity = Math.max(0, Number(value) || 0);
  return Number.isFinite(quantity) ? quantity : 0;
};

const normalizeProductId = (value) => {
  const productId = Number(value);
  return Number.isInteger(productId) ? productId : null;
};

const cloneOptions = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return { ...value };
  }
};

const normalizeSelectedOptions = (options, qtty) => {
  const next = cloneOptions(options);

  if (Array.isArray(next.names)) {
    next.names = next.names.slice(0, qtty);
  }

  if (next.box) {
    const boxQtty = Math.max(1, Number(next.boxQtty) || 1);
    next.boxQtty = Math.max(1, Math.min(boxQtty, qtty || 1));
  }

  return next;
};

const resolveLookupProduct = (entry, productLookup = {}) => {
  const resolvedEntry =
    productLookup?.[entry?.productId] ||
    productLookup?.[String(entry?.productId)] ||
    null;

  return resolvedEntry?.product || resolvedEntry || null;
};

const resolveProductPhotoUrl = (entry, productLookup = {}) => {
  const product = resolveLookupProduct(entry, productLookup);
  const rawPhoto = Array.isArray(product?.photos)
    ? product.photos.find(Boolean)
    : product?.photo || product?.image || product?.img;

  const photo = String(rawPhoto || "").trim();

  if (!photo) return "";
  if (/^https?:\/\//i.test(photo)) return photo.replace(/^http:\/\//i, "https://");

  const normalizedPhoto = photo.replace(/^\/+/, "");

  if (/^uploads\//i.test(normalizedPhoto)) {
    return `https://orizom.com.br/${normalizedPhoto}`;
  }

  return `https://orizom.com.br/uploads/thumbs/${normalizedPhoto}`;
};

export const getProductsByIdFromResponse = (payload) => {
  const directCollection = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.products)
    ? payload.products
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.rows)
    ? payload.rows
    : Array.isArray(payload?.data)
    ? payload.data
    : null;

  if (directCollection) {
    return directCollection.reduce((acc, item) => {
      const productId = Number(item?.id ?? item?.productId);
      if (!Number.isInteger(productId)) return acc;
      acc[productId] = item;
      return acc;
    }, {});
  }

  if (payload && typeof payload === "object") {
    return Object.values(payload).reduce((acc, item) => {
      const productId = Number(item?.id ?? item?.productId);
      if (!Number.isInteger(productId)) return acc;
      acc[productId] = item;
      return acc;
    }, {});
  }

  return {};
};

export const productNeedsFullPackagingDetails = (product) =>
  !product ||
  ((product?.fullPackagingId === undefined ||
    product?.fullPackagingId === null ||
    product?.fullPackagingId === "") &&
    (product?.packagingId === undefined ||
      product?.packagingId === null ||
      product?.packagingId === ""));

export const getUploadCartProductIdsNeedingDetails = (
  uploadCart = [],
  productLookup = {}
) =>
  normalizeUploadCart(uploadCart)
    .map((item) => Number(item?.productId))
    .filter((productId, index, ids) => {
      if (!Number.isInteger(productId) || ids.indexOf(productId) !== index) {
        return false;
      }

      const product =
        productLookup?.[productId] || productLookup?.[String(productId)] || null;
      return productNeedsFullPackagingDetails(product);
    });

export const normalizeUploadCart = (cart = []) => {
  const quantitiesByProductId = new Map();

  (Array.isArray(cart) ? cart : []).forEach((item) => {
    const productId = normalizeProductId(item?.productId ?? item?.id);
    const qtty = normalizeQuantity(item?.qtty ?? item?.qtde ?? item?.quantity);
    const sku = String(item?.sku || "").trim();

    if (productId === null || qtty <= 0) return;

    const currentEntry = quantitiesByProductId.get(productId);

    if (currentEntry) {
      currentEntry.qtty += qtty;
      if (!currentEntry.sku && sku) currentEntry.sku = sku;
      return;
    }

    quantitiesByProductId.set(productId, {
      productId,
      qtty,
      sku: sku || null,
    });
  });

  return Array.from(quantitiesByProductId.values());
};

const sortCartEntries = (entries = []) =>
  [...entries].sort((left, right) => left.productId - right.productId);

export const areUploadCartEntriesDifferent = (left = [], right = []) => {
  const normalizedLeft = sortCartEntries(normalizeUploadCart(left));
  const normalizedRight = sortCartEntries(normalizeUploadCart(right));

  if (normalizedLeft.length !== normalizedRight.length) return true;

  return normalizedLeft.some((entry, index) => {
    const other = normalizedRight[index];
    return (
      entry.productId !== other?.productId || entry.qtty !== other?.qtty
    );
  });
};

export const getCatalogCartEntries = (selected = []) =>
  normalizeUploadCart(
    (Array.isArray(selected) ? selected : []).map((item) => ({
      productId: item?.id,
      qtty: item?.qtde,
    }))
  );

export const getFullCartEntries = (cart = []) =>
  normalizeUploadCart(
    (Array.isArray(cart) ? cart : []).map((item) => ({
      productId: item?.product?.id,
      qtty: item?.quantity,
    }))
  );

export const buildCatalogSelectionFromUploadCart = (uploadCart, currentSelected = []) => {
  const selectedById = new Map(
    (Array.isArray(currentSelected) ? currentSelected : []).map((item) => [
      normalizeProductId(item?.id),
      item,
    ])
  );

  return normalizeUploadCart(uploadCart).map(({ productId, qtty }) => {
    const currentItem = selectedById.get(productId);

    return {
      id: productId,
      qtde: qtty,
      options: normalizeSelectedOptions(currentItem?.options, qtty),
    };
  });
};

export const buildFullCartFromUploadCart = (
  uploadCart,
  currentCart = [],
  productsById = {}
) => {
  const currentCartById = new Map(
    (Array.isArray(currentCart) ? currentCart : []).map((item) => [
      normalizeProductId(item?.product?.id),
      item,
    ])
  );

  return normalizeUploadCart(uploadCart)
    .map(({ productId, qtty }) => {
      const currentItem = currentCartById.get(productId);
      const resolvedEntry =
        currentItem ||
        productsById?.[productId] ||
        productsById?.[String(productId)] ||
        null;
      const product =
        currentItem?.product ||
        resolvedEntry?.product ||
        resolvedEntry ||
        null;

      if (!product) return null;

      return {
        ...(resolvedEntry?.product ? resolvedEntry : {}),
        ...currentItem,
        id: currentItem?.id ?? resolvedEntry?.id ?? productId,
        idMeli:
          currentItem?.idMeli ??
          resolvedEntry?.idMeli ??
          product?.idMeli ??
          null,
        product,
        quantity: qtty,
      };
    })
    .filter(Boolean);
};

const resolveProductLabel = (entry, productLookup = {}) => {
  const product = resolveLookupProduct(entry, productLookup);

  return product?.sku || entry?.sku || `Produto #${entry?.productId ?? "-"}`;
};

const buildCartCardsHtml = (entries, productLookup = {}, emptyLabel) => {
  const normalizedEntries = normalizeUploadCart(entries);

  if (!normalizedEntries.length) {
    return `<div class="upload-cart-sync-empty">${escapeHtml(emptyLabel)}</div>`;
  }

  return normalizedEntries
    .map((entry) => {
      const label = escapeHtml(resolveProductLabel(entry, productLookup));
      const photoUrl = resolveProductPhotoUrl(entry, productLookup);
      const labelHtml = `<div class="upload-cart-sync-card-label" title="${label}">${label}</div>`;
      const quantityBadgeHtml =
        entry.qtty > 1
          ? `<div class="upload-cart-sync-card-badge" title="Quantidade detectada: ${escapeHtml(
              entry.qtty
            )}" aria-label="Quantidade detectada: ${escapeHtml(entry.qtty)}">${escapeHtml(
              entry.qtty
            )}</div>`
          : "";
      const mediaHtml = photoUrl
        ? `<div class="upload-cart-sync-card-media">${quantityBadgeHtml}<img src="${escapeHtml(
            photoUrl
          )}" alt="${label}" />${labelHtml}</div>`
        : `<div class="upload-cart-sync-card-media upload-cart-sync-card-media-placeholder">${quantityBadgeHtml}<span aria-hidden="true">?</span>${labelHtml}</div>`;

      return `
        <div class="upload-cart-sync-card">
          ${mediaHtml}
        </div>
      `;
    })
    .join("");
};

export const buildUploadCartSyncHtml = (
  currentEntries,
  nextEntries,
  productLookup = {}
) => `
  <div class="upload-cart-sync">
    <p class="upload-cart-sync-intro">
      O documento enviado indica um carrinho diferente do atual. <br>Deseja substituir os itens selecionados?
    </p>
    <div class="upload-cart-sync-section">
      <p class="upload-cart-sync-section-title">Carrinho atual</p>
      <div class="upload-cart-sync-list">
        ${buildCartCardsHtml(currentEntries, productLookup, "Nenhum item selecionado.")}
      </div>
    </div>
    <div class="upload-cart-sync-section">
      <p class="upload-cart-sync-section-title">Carrinho detectado</p>
      <div class="upload-cart-sync-list">
        ${buildCartCardsHtml(nextEntries, productLookup, "Nenhum item detectado na etiqueta.")}
      </div>
    </div>
  </div>
`;
